import styled from "styled-components";
import type { DefaultTheme } from "styled-components";
import { ColorType, Text } from "@ifgengineering/component-library";
import type { BranchSemanticColour } from "@ifgengineering/design-tokens/legacy";
import { branch } from "@styles/branchColour";
import { isOnboardingSurface } from "@styles/onboardingSurface";

/**
 * The shared Text component pins a colour from the light palette, so on the dark
 * page its copy comes out near-black. This maps the palette names the reskinned
 * screens actually pass onto theme tokens whose light values are the same, and
 * wins them back on specificity.
 *
 * A drop-in replacement: swap `Text` for `ThemedText` and keep every prop.
 */
const TOKEN: Partial<Record<ColorType, (theme: DefaultTheme) => string>> = {
  BLACK: (theme) => theme.colors.ui.text.primary,
  SLATE900: (theme) => theme.colors.ui.text.primary,
  SLATE800: (theme) => theme.colors.ui.text.primary,
  MSLATE700: (theme) => theme.colors.ui.text.secondary,
  MSLATE900: (theme) => theme.colors.ui.text.primary,
  GSLATE700: (theme) => theme.colors.ui.text.secondary,
  SLATE700: (theme) => theme.colors.ui.text.secondary,
  SLATE600: (theme) => theme.colors.ui.text.tertiary,
  SLATE500: (theme) => theme.colors.ui.text.tertiary,
  SLATE450: (theme) => theme.colors.ui.text.disabled,
  SLATE400: (theme) => theme.colors.ui.text.disabled,
  // The pale end of the scale is only ever used as light ink on a coloured or
  // fixed-dark surface, so it maps to onColor, which stays light in both
  // palettes. text.inverse would be a dark ink on the dark one.
  WHITE: (theme) => theme.colors.ui.text.onColor,
  SLATE100: (theme) => theme.colors.ui.text.onColor,
  SLATE200: (theme) => theme.colors.ui.text.onColor,
  SLATE300: (theme) => theme.colors.ui.text.onColor,
  BLUE900: (theme) => theme.colors.ui.text.accent,
  BLUE600: (theme) => theme.colors.ui.text.brand,
  BLUE700: (theme) => theme.colors.ui.text.accent,
  BLUE800: (theme) => theme.colors.ui.text.accent,
  GREEN800: (theme) => theme.colors.ui.text.success,
  SUCCESS800: (theme) => theme.colors.ui.text.success,
  ERROR800: (theme) => theme.colors.ui.text.error,
};

/**
 * An unmapped name is left to the shared component rather than guessed at — a
 * wrong guess is how pale ink on a coloured surface turns dark.
 */
const resolve = (theme: DefaultTheme, color?: ColorType): string | null => {
  const token = color ? TOKEN[color] : TOKEN.SLATE900;
  return token ? token(theme) : null;
};

export const ThemedText = styled(Text)`
  ${({ theme, color }) => {
    const resolved = resolve(theme, color as ColorType);
    return resolved ? `&& { color: ${resolved}; }` : "";
  }}
`;

/** The palette names the onboarding's fields pass, by the Figma colour each one now stands for. */
const ONBOARDING_TEXT: Partial<Record<ColorType, BranchSemanticColour>> = {
  SLATE900: "text/primary",
  SLATE800: "text/primary",
  SLATE700: "text/secondary",
  SLATE600: "text/tertiary",
  SLATE500: "text/tertiary",
  SLATE450: "text/disabled",
  SLATE400: "text/disabled",
  GREEN800: "text/success",
  SUCCESS800: "text/success",
  ERROR800: "text/error",
};

const resolveOnSurface = (
  theme: DefaultTheme,
  color?: ColorType
): string | null => {
  const name = ONBOARDING_TEXT[color ?? "SLATE900"];
  return name ? branch(theme, name) : resolve(theme, color);
};

/** ThemedText's dark mapping inside the deal-anchored onboarding only, so shared screens keep their look elsewhere. */
export const OnboardingSurfaceText = styled(Text)`
  ${({ theme, color }) => {
    const resolved = isOnboardingSurface(theme)
      ? resolveOnSurface(theme, color as ColorType)
      : null;
    return resolved ? `&& { color: ${resolved}; }` : "";
  }}
`;

export default ThemedText;
